import React, { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';
import { technicianService } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { useTeam } from '../context/TeamContext';

const TechniciansContext = createContext(null);

export const useTechnicians = () => {
  const context = useContext(TechniciansContext);
  if (!context) {
    throw new Error('useTechnicians must be used within a TechniciansProvider');
  }
  return context;
};

export const TechniciansProvider = ({ children }) => {
  const [technicians, setTechnicians] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const { currentUser } = useAuth();
  const { selectedTeam } = useTeam();
  const [initialized, setInitialized] = useState(false);

  // Log pour diagnostiquer selectedTeam
  useEffect(() => {
    console.log('🔍 TechniciansContext - selectedTeam changé:', selectedTeam);
  }, [selectedTeam]);

  // Flag pour éviter les appels multiples
  const isLoadingRef = useRef(false);

    // Charger tous les techniciens
  const loadTechnicians = useCallback(async (forceReload = false, teamOverride = null) => {
    // Si déjà en cours de chargement, ne pas relancer
    if (isLoadingRef.current && !forceReload) {
      console.log('⏳ TechniciansContext - Chargement déjà en cours, abandon');
      return;
    }

    // Si pas d'utilisateur connecté, nettoyer
    if (!currentUser?.id) {
      console.log('👤 TechniciansContext - Utilisateur non connecté:', {
        userId: currentUser?.id,
        userEmail: currentUser?.email
      });
      setTechnicians([]);
      setLoading(false);
      setError(null);
      return;
    }

    try {
      isLoadingRef.current = true;
      setLoading(true);
      setError(null);
      
      const teamToUse = teamOverride || selectedTeam;
      console.log('👥 TechniciansContext - Chargement des techniciens pour:', currentUser?.email, 'équipe:', teamToUse?.name);

      const data = await technicianService.getAll(teamToUse?.id);
      setTechnicians(data);
      setInitialized(true);
    } catch (err) {
      console.error('❌ TechniciansContext - Erreur:', err);
      setError(err.message);
      setTechnicians([]);
    } finally {
      setLoading(false);
      isLoadingRef.current = false;
    }
  }, [currentUser?.id, currentUser?.email, selectedTeam?.id]);

  // Nettoyer les données quand l'utilisateur se déconnecte
  useEffect(() => {
    if (!currentUser?.id && initialized) {
      console.log('🧹 TechniciansContext - Nettoyage des données utilisateur');
      setTechnicians([]);
      setError(null);
      setInitialized(false);
      setLoading(false);
    }
  }, [currentUser?.id, initialized]);

  // Écouter les changements d'équipe
  useEffect(() => {
    const handleTeamChange = (event) => {
      const { team, userId } = event.detail;
      
      console.log('📡 TechniciansContext - Événement teamChanged reçu:', team.name, 'pour userId:', userId, 'currentUser:', currentUser?.id);
      if (userId === currentUser?.id) {
        console.log('🔄 TechniciansContext - Changement d\'équipe détecté:', team.name);
        loadTechnicians(true, team); // Force le rechargement avec la nouvelle équipe
      }
    };

    window.addEventListener('teamChanged', handleTeamChange);
    
    return () => {
      window.removeEventListener('teamChanged', handleTeamChange);
    };
  }, [currentUser?.id, loadTechnicians]);

  // Ajouter un technicien
  const addTechnician = useCallback(async (technicianData) => {
    try {
      setError(null);
      console.log('➕ Ajout technicien:', technicianData);
      console.log('🎯 Équipe sélectionnée pour création:', selectedTeam);
      console.log('🔍 DEBUG addTechnician - Valeurs au moment de l\'appel:', {
        selectedTeam,
        selectedTeamId: selectedTeam?.id,
        selectedTeamName: selectedTeam?.name
      });
      
      if (!selectedTeam || !selectedTeam.id) {
        console.error('❌ selectedTeam est null:', { selectedTeam });
        throw new Error('Aucune équipe sélectionnée pour créer le technicien');
      }
      
      const technicianDataWithTeam = {
        ...technicianData,
        teamId: selectedTeam.id
      };
      
      console.log('📤 Données envoyées avec teamId:', technicianDataWithTeam);
      
      const newTechnician = await technicianService.create(technicianDataWithTeam);
      setTechnicians(prev => [...prev, newTechnician].sort((a, b) => 
        (a.lastName || '').localeCompare(b.lastName || '') ||
        (a.firstName || '').localeCompare(b.firstName || '')
      ));
      console.log('✅ Technicien ajouté:', newTechnician);
      
      return newTechnician;
    } catch (err) {
      console.error('❌ Erreur lors de l\'ajout du technicien:', err);
      setError(err.message || 'Erreur lors de l\'ajout du technicien');
      throw err;
    }
  }, [selectedTeam]);

  // Mettre à jour un technicien
  const updateTechnician = useCallback(async (id, technicianData) => {
    try {
      setError(null);
      console.log('✏️ Mise à jour technicien:', id, technicianData);
      
      const technicianDataWithTeam = {
        ...technicianData,
        teamId: selectedTeam?.id
      };
      
      const updatedTechnician = await technicianService.update(id, technicianDataWithTeam);
      setTechnicians(prev => 
        prev.map(tech => tech.id === id ? updatedTechnician : tech)
            .sort((a, b) => 
              (a.lastName || '').localeCompare(b.lastName || '') ||
              (a.firstName || '').localeCompare(b.firstName || '')
            )
      );
      console.log('✅ Technicien mis à jour:', updatedTechnician);
      
      return updatedTechnician;
    } catch (err) {
      console.error('❌ Erreur lors de la mise à jour du technicien:', err);
      setError(err.message || 'Erreur lors de la mise à jour du technicien');
      throw err;
    }
  }, [selectedTeam]);

  // Supprimer un technicien
  const deleteTechnician = useCallback(async (id) => {
    try {
      setError(null);
      console.log('🗑️ Suppression technicien:', id);
      
      await technicianService.delete(id);
      setTechnicians(prev => prev.filter(tech => tech.id !== id));
      console.log('✅ Technicien supprimé:', id);
    } catch (err) {
      console.error('❌ Erreur lors de la suppression du technicien:', err);
      setError(err.message || 'Erreur lors de la suppression du technicien');
      throw err;
    }
  }, []);

  // Récupérer un technicien par ID
  const getTechnicianById = useCallback((id) => {
    return technicians.find(tech => tech.id === parseInt(id));
  }, [technicians]);

  // Fonction d'initialisation manuelle
  const initializeData = useCallback(async (userOverride = null) => {
    const userToUse = userOverride || currentUser;
    
    if (!userToUse?.id) {
      console.log('⚠️ TechniciansContext - Initialisation sans utilisateur connecté');
      return;
    }
    
    console.log('🔄 TechniciansContext - Initialisation manuelle pour', userToUse.email);
    setInitialized(false);
    setLoading(true);
    setError(null);
    
    try {
      await loadTechnicians(true);
      setInitialized(true);
      console.log('✅ TechniciansContext - Initialisation terminée');
    } catch (error) {
      console.error('❌ TechniciansContext - Erreur lors de l\'initialisation:', error);
      setError(error.message || 'Erreur lors de l\'initialisation');
      setTechnicians([]);
    } finally {
      setLoading(false);
    }
  }, [loadTechnicians, currentUser]);

  const value = {
    // État
    technicians,
    loading,
    error,
    initialized,
    
    // Actions
    loadTechnicians,
    addTechnician,
    updateTechnician,
    deleteTechnician,
    
    // Utilitaires
    getTechnicianById,
    
    // Stats
    totalTechnicians: technicians.length,
    
    // Initialisation
    initializeData,
  };

  return (
    <TechniciansContext.Provider value={value}>
      {children}
    </TechniciansContext.Provider>
  );
}; 